import type { LifeSpace, Project } from '@/src/renderer/lib/db/models'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { LucideIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DndContext, DragOverlay, useDraggable, useSensor, useSensors, type DragMoveEvent, type DragStartEvent } from '@dnd-kit/core'
import { getEventCoordinates } from '@dnd-kit/utilities'
import { showErrorToast } from '@/src/renderer/components/common/MessageToast'
import { showUndoToast } from '@/src/renderer/components/common/UndoToast'
import { AreaIcon } from '../../../../components/icons/AreaIcon'
import { PlusIcon } from '../../../../components/icons/PlusIcon'
import { useDataSource } from '../../../../lib/dataSource'
import { useIntroNote } from '../../../../lib/intro'
import {
  flattenProjectTree,
  MAX_PROJECT_DEPTH,
  projectAncestorIds,
  projectDescendantIds,
  projectDropTarget,
  projectSubtreeHeight,
  type MeasuredProjectRow,
  type ProjectDropTarget,
  type ProjectTreeRow,
} from '../../../../lib/projectTree'
import { SidebarSection } from '../SidebarSection'
import { ProjectSidebarItem } from '../ProjectSidebarItem'
import type { ProjectSidebarItemProps } from '../ProjectSidebarItem'
import { ProjectDragOverlay } from '../ProjectDragOverlay'
import { ProjectDropIndicator } from '../ProjectDropIndicator'
import { useProjectExpand } from '../useProjectExpand'
import { useSidebarData } from '../SidebarDataContext'
import { ProjectPointerSensor } from './projectPointerSensor'
import { cardBesideCursor } from './cardBesideCursor'
import type { ProjectsSectionProps } from './ProjectsSection.types'
import './ProjectsSection.scss'

/** Was beim Anfassen feststeht: der Ast und die gemessenen Zeilen. */
interface DragSession {
  project: Project
  branchIds: ReadonlySet<string>
  height: number
  rows: MeasuredProjectRow[]
  /** Oberkante der Liste beim Anfassen — die Linie liegt in der Liste. */
  treeTop: number
}

/** Eine Zeile des Baums als Griff für dnd-kit. Nur hier: Die Story von `ProjectSidebarItem` braucht kein `DndContext`. */
const DraggableProjectRow: React.FC<ProjectSidebarItemProps & { onRowElement: (id: string, element: HTMLElement | null) => void }> = ({ onRowElement, ...props }) => {
  const { setNodeRef, listeners } = useDraggable({ id: props.project.id })
  const id = props.project.id
  const dragHandle = useMemo(() => ({
    ref: (element: HTMLElement | null) => {
      setNodeRef(element)
      onRowElement(id, element)
    },
    onPointerDown: listeners?.onPointerDown as ((event: React.PointerEvent<HTMLElement>) => void) | undefined,
  }), [setNodeRef, onRowElement, id, listeners])
  return <ProjectSidebarItem {...props} dragHandle={dragHandle} />
}

export const ProjectsSection: React.FC<ProjectsSectionProps> = ({
  lifeSpace,
  activeTab,
  openTab,
  navigateActiveTab,
  onCloseTabByPath,
  onUnpinByPath,
  onPinByPath,
  openPaths,
  previewPaths,
  pinnedPaths,
  onOpenTab,
  onDoubleClick,
  onPromote,
}) => {
  const { t } = useTranslation()
  const source = useDataSource()
  const noteIntro = useIntroNote()
  // Aus dem Halter oberhalb des Wechsels (`SidebarDataProvider`): eine eigene Abfrage begänne beim
  // Neu-Einhängen jedes Mal bei `undefined`.
  const { projects } = useSidebarData(lifeSpace)
  const { createProject, deleteProject, restoreProject, moveProjectInTree } = source
  const { collapsedProjects, toggleProjectExpand, expandProjects } = useProjectExpand(lifeSpace)
  const rows = useMemo(() => flattenProjectTree(projects ?? [], collapsedProjects), [projects, collapsedProjects])
  const visibleRows = useMemo(() => rows.filter(r => !r.hidden), [rows])
  /** Wie viele Unterprojekte unter jedem Projekt hängen, zugeklappte mitgezählt. */
  const subprojectCounts = useMemo(() => {
    const counts = new Map<string, number>()
    rows.forEach((row, i) => {
      let j = i + 1
      while (j < rows.length && rows[j].depth > row.depth) j++
      counts.set(row.id, j - i - 1)
    })
    return counts
  }, [rows])
  const pathOf = useCallback((projectId: string) => `/home/${lifeSpace}/project/${projectId}`, [lifeSpace])
  // Neu favorisiert landet unten in „Favoriten“: die Zeit in ms ist größer als jede frühere Stelle (#210).
  const setProjectFavorite = useCallback(
    (projectId: string, favorite: boolean) => source.updateProject(projectId, { favoriteOrder: favorite ? Date.now() : null }),
    [source],
  )

  /*
    **Liegt das aktive Projekt in einem zugeklappten Ast, klappen seine Vorfahren auf** —
    einmal je Wechsel des aktiven Projekts. Wer danach selbst zuklappt, bleibt zugeklappt.
  */
  const activeProjectId = useMemo(() => {
    const prefix = `/home/${lifeSpace}/project/`
    return activeTab?.path.startsWith(prefix) ? activeTab.path.slice(prefix.length).split(/[/?]/)[0] : null
  }, [activeTab?.path, lifeSpace])
  const revealedRef = useRef<string | null>(null)
  useEffect(() => {
    if (!projects || !activeProjectId || revealedRef.current === activeProjectId) return
    revealedRef.current = activeProjectId
    expandProjects(projectAncestorIds(projects, activeProjectId))
  }, [projects, activeProjectId, expandProjects])

  const handleCreateProject = async (parent?: Project) => {
    try {
      const project = await createProject({
        name: '',
        description: null,
        color: 'var(--space-500)',
        status: 'active',
        lifeSpace: lifeSpace as LifeSpace,
        startDate: null,
        endDate: null,
        progress: 0,
        ...(parent ? { parentId: parent.id } : {}),
      })
      noteIntro('projects')
      if (parent) expandProjects([parent.id, ...projectAncestorIds(projects ?? [], parent.id)])
      navigateActiveTab({
        path: pathOf(project.id),
        title: t('project.defaultName'),
        icon: AreaIcon,
      })
    } catch (err) {
      // Without this the click silently does nothing: no project, no navigation,
      // and — since the action button expands the group — an empty open group.
      console.error('Failed to create project:', err)
      showErrorToast(t('project.createFailed'))
    }
  }

  const handleDelete = async (project: Project) => {
    /*
      **Löschen mit Rückweg** (Abnahme 21.09.2026, F-PR5). Es war die
      einzige löschende Handlung ohne Rückgängig — und sie nimmt ein
      ganzes Projekt samt Aufgaben mit, seit #185 samt Unterprojekten.

      **Die Tabs kommen mit zurück.** Rückgängig heißt: es ist wie
      vorher, und vorher standen die Projekte offen. Gemerkt wird
      deshalb, WELCHE offen waren — wer keins offen hatte, bekommt auch keins.
    */
    const branch = [project.id, ...projectDescendantIds(projects ?? [], project.id)]
    const hadTabs = branch.filter(id => openPaths.has(pathOf(id)))
    const names = new Map((projects ?? []).map(p => [p.id, p.name || t('project.defaultName')]))
    const name = names.get(project.id) ?? t('project.defaultName')
    for (const id of branch) onCloseTabByPath?.(pathOf(id))
    const cascade = await deleteProject(project.id)
    const count = cascade.subprojectIds?.length ?? 0
    showUndoToast({
      message: count > 0 ? t('project.deletedWithSubprojects', { name, count }) : t('project.deleted', { name }),
      undoLabel: t('common.undo'),
      undoneMessage: t('undo.restored.project', { name }),
      onUndo: async () => {
        await restoreProject(project.id, cascade)
        for (const id of hadTabs) {
          openTab({
            id: `${lifeSpace}-project-${id}-${Date.now()}`,
            path: pathOf(id),
            title: names.get(id) ?? t('project.defaultName'),
            icon: AreaIcon as unknown as LucideIcon,
            lifeSpace,
            isDirty: false,
            isPinned: false,
          })
        }
      },
    })
  }

  /* ── Ziehen (Todoist-Stil): hoch/runter = Stelle, links/rechts = Ebene ── */

  const sensors = useSensors(useSensor(ProjectPointerSensor, { activationConstraint: { distance: 4 } }))
  const treeRef = useRef<HTMLDivElement>(null)
  const rowElements = useRef(new Map<string, HTMLElement>())
  const onRowElement = useCallback((id: string, element: HTMLElement | null) => {
    if (element) rowElements.current.set(id, element)
    else rowElements.current.delete(id)
  }, [])
  const [session, setSession] = useState<DragSession | null>(null)
  const [target, setTarget] = useState<ProjectDropTarget | null>(null)

  useEffect(() => {
    if (!session) return
    document.body.classList.add('project-tree-dragging')
    return () => document.body.classList.remove('project-tree-dragging')
  }, [session])

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const project = projects?.find(p => p.id === event.active.id)
    const tree = treeRef.current
    if (!project || !projects || !tree) return
    const measured: MeasuredProjectRow[] = []
    for (const row of visibleRows) {
      const rect = rowElements.current.get(row.id)?.getBoundingClientRect()
      if (rect) measured.push({ id: row.id, depth: row.depth, top: rect.top, bottom: rect.bottom })
    }
    setSession({
      project,
      branchIds: new Set([project.id, ...projectDescendantIds(projects, project.id)]),
      height: projectSubtreeHeight(projects, project.id),
      rows: measured,
      treeTop: tree.getBoundingClientRect().top,
    })
    setTarget(null)
  }, [projects, visibleRows])

  const handleDragMove = useCallback((event: DragMoveEvent) => {
    const pointer = getEventCoordinates(event.activatorEvent)
    if (!session || !pointer) return
    /*
      Die Fuge folgt dem Zeiger — die Karte steht mittig neben ihm (`cardBesideCursor`).
      `delta` rechnet ein Scrollen der Sidebar mit ein — die beim Anfassen gemessenen Zeilen
      bleiben gültig.
    */
    const next = projectDropTarget({
      rows: session.rows,
      draggedId: session.project.id,
      centerY: pointer.y + event.delta.y,
      offsetX: event.delta.x,
      height: session.height,
    })
    // Nur neu zeichnen, wenn die Linie wirklich springt — nicht bei jedem Pixel.
    setTarget(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next))
  }, [session])

  const endDrag = useCallback(() => {
    setSession(null)
    setTarget(null)
  }, [])

  const handleDragEnd = useCallback(async () => {
    const drop = session && target?.projection.ok ? { id: session.project.id, ...target.projection } : null
    endDrag()
    if (!drop) return
    // Kein ⌘Z: ADR 0028 nimmt Reihenfolge und Verschieben aus.
    const moved = await moveProjectInTree(drop.id, { parentId: drop.parentId, afterId: drop.afterId })
    if (moved && drop.parentId) expandProjects([drop.parentId])
  }, [session, target, endDrag, moveProjectInTree, expandProjects])

  const renderRow = (row: ProjectTreeRow<Project>) => {
    const { project } = row
    return (
      <DraggableProjectRow
        key={project.id}
        onRowElement={onRowElement}
        project={project}
        lifeSpace={lifeSpace}
        activeTab={activeTab}
        depth={row.depth}
        hasChildren={row.hasChildren}
        expanded={!collapsedProjects.has(project.id)}
        onToggleExpand={() => toggleProjectExpand(project.id)}
        openPaths={openPaths}
        previewPaths={previewPaths}
        pinnedPaths={pinnedPaths}
        onOpenTab={onOpenTab}
        onDoubleClick={onDoubleClick}
        onPromote={onPromote}
        onCloseTabByPath={onCloseTabByPath}
        onUnpinByPath={onUnpinByPath}
        onPinByPath={onPinByPath}
        openTab={openTab}
        onDelete={() => { void handleDelete(project) }}
        subprojectCount={subprojectCounts.get(project.id) ?? 0}
        onAddSubproject={row.depth + 1 < MAX_PROJECT_DEPTH ? () => { void handleCreateProject(project) } : undefined}
        favorite={project.favoriteOrder != null}
        onFavoriteChange={favorite => { void setProjectFavorite(project.id, favorite) }}
        dragging={session?.project.id === project.id}
        dimmed={session !== null && session.project.id !== project.id && session.branchIds.has(project.id)}
      />
    )
  }

  return (
    <SidebarSection
      id={`${lifeSpace}-projects`}
      title={t('sidebar.projects')}
      defaultExpanded
      actionIcon={PlusIcon}
      actionLabel={t('sidebar.createProject')}
      onAction={() => { void handleCreateProject() }}
    >
      <DndContext
        sensors={sensors}
        onDragStart={handleDragStart}
        onDragMove={handleDragMove}
        onDragEnd={() => { void handleDragEnd() }}
        onDragCancel={endDrag}
      >
        <div
          ref={treeRef}
          role="tree"
          aria-label={t('sidebar.projects')}
          className={`projects-tree${session ? ' projects-tree--dragging' : ''}`}
        >
          {visibleRows.map(renderRow)}
          {session && target && (
            <ProjectDropIndicator
              top={target.lineY - session.treeTop}
              depth={target.projection.depth}
              blocked={!target.projection.ok}
            />
          )}
        </div>
        {createPortal(
          <DragOverlay dropAnimation={null} modifiers={[cardBesideCursor]}>
            {session && (
              <ProjectDragOverlay project={session.project} subprojectCount={session.branchIds.size - 1} />
            )}
          </DragOverlay>,
          document.body,
        )}
      </DndContext>
    </SidebarSection>
  )
}
